// Which of a row of equal-width icon buttons fit in the space there is, and
// which move behind a "⋯" menu. The menu button takes a slot of its own as
// soon as anything overflows, so it is counted before deciding what goes.
// An action marked `menuOnly` never takes a slot: it is always in the menu,
// so the menu is always there when one exists.

/**
 * @param {Array<{key: string, menuOnly?: boolean}>} all The row, in display order.
 * @param {{available: number, buttonWidth: number, gap: number,
 *     overflowOrder: Array<string>}} space `overflowOrder` lists keys in the
 *   order they leave the row; keys not in it leave after those, from the end
 *   of the row backwards.
 * @returns {{visible: Array, overflow: Array}} Both in display order.
 */
export function fitActions(all, { available, buttonWidth, gap, overflowOrder = [] }) {
  const menuOnly = new Set(all.filter((a) => a.menuOnly).map((a) => a.key))
  const actions = all.filter((a) => !menuOnly.has(a.key))
  const inMenu = (gone) => all.filter((a) => menuOnly.has(a.key) || gone.has(a.key))
  const width = (n) => (n > 0 ? n * buttonWidth + (n - 1) * gap : 0)
  // Not measured yet: show everything rather than flash an empty row.
  const measured = available > 0 && buttonWidth > 0
  const menuSlots = menuOnly.size > 0 ? 1 : 0
  if (!measured || width(actions.length + menuSlots) <= available) {
    return { visible: actions, overflow: inMenu(new Set()) }
  }

  // Room for the menu button plus how many others.
  const slots = Math.max(0, Math.floor((available + gap) / (buttonWidth + gap)) - 1)
  const keys = actions.map((a) => a.key)
  const leaving = [
    ...overflowOrder.filter((k) => keys.includes(k)),
    ...[...keys].reverse().filter((k) => !overflowOrder.includes(k)),
  ].slice(0, Math.max(0, actions.length - slots))
  const gone = new Set(leaving)

  return {
    visible: actions.filter((a) => !gone.has(a.key)),
    overflow: inMenu(gone),
  }
}
